import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import {
  addAddress,
  deleteAddress,
  fetchAddresses,
  updateAddress,
} from "../../services/api";
import { ADDRESS_TYPE } from "../../utils/constants";
import Loader from "../common/Loader";
import "../common/Account.css";

const emptyForm = {
  addressId: "",
  address: "",
  houseNumber: "",
  pinCode: "",
  area: "",
  state: "",
  locality: "",
  landmark: "",
  type: ADDRESS_TYPE.HOME,
};

const Addresses = () => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchAddresses();
      setList(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(err.message || "Failed to load addresses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        ...form,
        type: Number(form.type),
      };
      if (form.addressId) {
        await updateAddress(payload);
        setSuccess("Address updated");
      } else {
        const { addressId, ...rest } = payload;
        await addAddress(rest);
        setSuccess("Address added");
      }
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err.message || "Failed to save address");
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (item) => {
    setForm({
      addressId: item._id,
      address: item.address || "",
      houseNumber: item.houseNumber || "",
      pinCode: item.pinCode || "",
      area: item.area || "",
      state: item.state || "",
      locality: item.locality || "",
      landmark: item.landmark || "",
      type: item.type || ADDRESS_TYPE.HOME,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onDelete = async (addressId) => {
    if (!window.confirm("Delete this address?")) return;
    setSaving(true);
    try {
      await deleteAddress(addressId);
      setSuccess("Address deleted");
      await load();
    } catch (err) {
      setError(err.message || "Failed to delete");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="account-page">
      {(loading || saving) && (
        <Loader overlay message={saving ? "Saving..." : "Loading..."} />
      )}
      <div className="coustom_container">
        <h1>My Addresses</h1>
        <Row className="gy-4">
          <Col lg={5}>
            <div className="account-card">
              <h2 className="h5">{form.addressId ? "Edit address" : "Add address"}</h2>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}
              <Form onSubmit={onSubmit}>
                <Form.Group className="mb-2">
                  <Form.Label>House / Flat</Form.Label>
                  <Form.Control
                    name="houseNumber"
                    value={form.houseNumber}
                    onChange={onChange}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    name="address"
                    value={form.address}
                    onChange={onChange}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Locality</Form.Label>
                  <Form.Control
                    name="locality"
                    value={form.locality}
                    onChange={onChange}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Area</Form.Label>
                  <Form.Control name="area" value={form.area} onChange={onChange} />
                </Form.Group>
                <Row>
                  <Col>
                    <Form.Group className="mb-2">
                      <Form.Label>State</Form.Label>
                      <Form.Control
                        name="state"
                        value={form.state}
                        onChange={onChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-2">
                      <Form.Label>Pincode</Form.Label>
                      <Form.Control
                        name="pinCode"
                        value={form.pinCode}
                        onChange={onChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Type</Form.Label>
                  <Form.Select name="type" value={form.type} onChange={onChange}>
                    <option value={ADDRESS_TYPE.HOME}>Home</option>
                    <option value={ADDRESS_TYPE.WORK}>Work</option>
                    <option value={ADDRESS_TYPE.OTHER}>Other</option>
                  </Form.Select>
                </Form.Group>
                <div className="d-flex gap-2">
                  <Button type="submit" className="btn-kior">
                    {form.addressId ? "Update" : "Add"}
                  </Button>
                  {form.addressId && (
                    <Button
                      type="button"
                      className="btn-kior-outline"
                      onClick={() => setForm(emptyForm)}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </Form>
            </div>
          </Col>
          <Col lg={7}>
            <div className="account-card">
              <h2 className="h5">Saved addresses</h2>
              {!loading && list.length === 0 && (
                <p className="text-muted mb-0">No addresses yet.</p>
              )}
              {list.map((item) => (
                <div key={item._id} className="booking-item">
                  <h5>
                    {item.type === 1 ? "Home" : item.type === 2 ? "Work" : "Other"}
                  </h5>
                  <p className="mb-2 text-muted">
                    {[item.houseNumber, item.address, item.locality, item.area, item.state, item.pinCode]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                  <div className="d-flex gap-2">
                    <Button size="sm" className="btn-kior" onClick={() => onEdit(item)}>
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => onDelete(item._id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Addresses;
